import { useState, useRef, useEffect, useCallback } from "react";
import apiService from "../service/apiService";
import { useToast } from "./useToast";

export type GalleryItem = {
  id: string;
  url: string;
  type: "IMAGE" | "VIDEO";
  isExisting: boolean;
  file?: File;
  order: number;
  status: "uploading" | "error" | "done";
};

interface UseGalleryManagerParams {
  activityId?: string;
  token?: string;
}

export function useGalleryManager({
  activityId,
  token,
}: UseGalleryManagerParams) {
  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toastError } = useToast();

  const uploadOne = useCallback(
    async (file: File, localId: string, order: number) => {
      if (!activityId) {
        toastError(
          "Enregistrez d'abord les informations de base avant d'ajouter des fichiers.",
        );
        setGalleryItems((prev) => prev.filter((i) => i.id !== localId));
        return;
      }

      const formData = new FormData();
      formData.append("file", file);
      formData.append("order", String(order));

      try {
        const res = await apiService.MakePostRequest(
          `activities/${activityId}/gallery`,
          formData,
          token,
          true,
        );
        const saved = res?.data || res;

        setGalleryItems((prev) =>
          prev.map((item) =>
            item.id === localId
              ? {
                  id: saved.id,
                  backendId: saved.id,
                  url: saved.url,
                  type: saved.type,
                  order: saved.order,
                  isExisting: true,
                  status: "done" as const,
                }
              : item,
          ),
        );
      } catch (error) {
        toastError("Échec de l'envoi d'un fichier");
        setGalleryItems((prev) =>
          prev.map((item) =>
            item.id === localId ? { ...item, status: "error" as const } : item,
          ),
        );
      }
    },
    [activityId, token, toastError],
  );

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const list = Array.from(files);

      setGalleryItems((prev) => {
        let nextOrder = prev.length;
        const placeholders: GalleryItem[] = list.map((file) => {
          const localId = `local-${Date.now()}-${Math.random()
            .toString(36)
            .slice(2, 7)}`;
          const order = nextOrder++;
          const item: GalleryItem = {
            id: localId,
            url: URL.createObjectURL(file),
            type: file.type.startsWith("video/") ? "VIDEO" : "IMAGE",
            isExisting: false,
            order,
            status: "uploading",
            file,
          };
          // fire-and-forget — uploads happen one file at a time as soon
          // as they're added, instead of being batched at save time
          uploadOne(file, localId, order);
          return item;
        });
        return [...prev, ...placeholders];
      });
    },
    [uploadOne],
  );

  const removeFile = (idx: number) => {
    setGalleryItems((prev) => {
      const item = prev[idx];
      if (item && !item.isExisting && item.url) URL.revokeObjectURL(item.url);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const clearAllGallery = () => {
    galleryItems.forEach((item) => {
      if (!item.isExisting && item.url) URL.revokeObjectURL(item.url);
    });
    setGalleryItems([]);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const handleItemDragStart = (index: number) => setDraggedIndex(index);

  const handleItemDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedIndex === null || draggedIndex === index) return;
    setGalleryItems((prev) => {
      const next = [...prev];
      const [dragged] = next.splice(draggedIndex, 1);
      next.splice(index, 0, dragged);
      return next;
    });
    setDraggedIndex(index);
  };

  const handleItemDragEnd = () => setDraggedIndex(null);

  useEffect(() => {
    return () => {
      galleryItems.forEach((item) => {
        if (!item.isExisting && item.url) URL.revokeObjectURL(item.url);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    galleryItems,
    setGalleryItems,
    isDragOver,
    setIsDragOver,
    draggedIndex,
    fileInputRef,
    addFiles,
    removeFile,
    clearAllGallery,
    handleDrop,
    handleItemDragStart,
    handleItemDragOver,
    handleItemDragEnd,
    uploadOne,
  };
}
